import { useEffect, useRef, useState } from "react";
import { Alert, Autocomplete, Box, Button, Chip, IconButton, Stack, TextField, Tooltip, Typography } from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  deleteComment, errorMessage, fetchCommentRecipients, fetchComments, postComment, type CommentRecipient,
} from "@/api/emailThreads";

// Matches EmailComment::MAX_BODY.
const MAX_BODY = 2000;

function fmtDate(iso: string) {
  return new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}

// Internal notes on the email thread, visible to everyone on the portal (never
// to the vendor). Every triage item in the same Gmail thread shares them.
// focus: scroll to and focus the comment box (right-click "Add internal comment").
export function EmailComments({ emailId, focus, onFocused }: { emailId: number; focus?: boolean; onFocused?: () => void }) {
  const inputRef = useRef<HTMLTextAreaElement>(null);
  useEffect(() => {
    if (!focus) return;
    // After the pane has laid out (the thread above it loads first).
    const t = window.setTimeout(() => {
      inputRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      inputRef.current?.focus();
      onFocused?.();
    }, 400);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focus]);
  const queryClient = useQueryClient();
  const key = ["email-comments", emailId];
  const comments = useQuery({ queryKey: key, queryFn: () => fetchComments(emailId) });
  const [body, setBody] = useState("");
  const [notify, setNotify] = useState<CommentRecipient[]>([]);
  const [notifyOpen, setNotifyOpen] = useState(false);
  const recipients = useQuery({
    queryKey: ["email-comment-recipients"],
    queryFn: fetchCommentRecipients,
    enabled: notifyOpen,
    staleTime: 5 * 60_000,
  });

  const post = useMutation({
    mutationFn: () => postComment(emailId, body, notify.map((u) => u.id)),
    onSuccess: () => {
      setBody("");
      setNotify([]);
      setNotifyOpen(false);
      void queryClient.invalidateQueries({ queryKey: key });
    },
  });
  const remove = useMutation({
    mutationFn: (id: number) => deleteComment(id),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: key }),
  });

  const list = comments.data ?? [];

  return (
    <Box>
      <Typography variant="caption" color="text.secondary">
        Internal comments{list.length > 0 ? ` · ${list.length}` : ""} (the vendor never sees these)
      </Typography>

      {list.length > 0 && (
        <Stack spacing={1} sx={{ mt: 0.5, mb: 1 }}>
          {list.map((c) => (
            <Box key={c.id} sx={{ p: 1.25, borderRadius: 1, bgcolor: c.mine ? "action.hover" : "grey.100", border: 1, borderColor: "divider" }}>
              <Stack direction="row" alignItems="baseline" spacing={1}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>{c.author}</Typography>
                <Typography variant="caption" color="text.secondary">{fmtDate(c.created_at)}</Typography>
                <Box sx={{ flex: 1 }} />
                {c.can_delete && (
                  <Tooltip title="Delete comment">
                    <IconButton size="small" onClick={() => remove.mutate(c.id)} disabled={remove.isPending} aria-label="Delete comment">
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </Stack>
              <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", mt: 0.25 }}>{c.body}</Typography>
              {c.notified.length > 0 && (
                <Typography variant="caption" color="text.secondary">Emailed to {c.notified.join(", ")}</Typography>
              )}
            </Box>
          ))}
        </Stack>
      )}

      <Stack spacing={1} sx={{ mt: list.length > 0 ? 0 : 0.5 }}>
        <TextField
          multiline
          minRows={2}
          maxRows={8}
          size="small"
          placeholder="Add an internal comment…"
          inputRef={inputRef}
          value={body}
          onChange={(e) => setBody(e.target.value.slice(0, MAX_BODY))}
          onKeyDown={(e) => {
            if (e.key === "Enter" && (e.metaKey || e.ctrlKey) && body.trim() && !post.isPending) post.mutate();
          }}
        />
        {notifyOpen && (
          <Autocomplete
            multiple
            size="small"
            options={recipients.data ?? []}
            loading={recipients.isLoading}
            value={notify}
            onChange={(_e, v) => setNotify(v)}
            getOptionLabel={(u) => (u.name ? `${u.name} (${u.email})` : u.email)}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            renderTags={(tags, getTagProps) => tags.map((u, i) => {
              const { key: tagKey, ...rest } = getTagProps({ index: i });
              return <Chip key={tagKey} {...rest} size="small" label={u.name || u.email} />;
            })}
            renderInput={(params) => <TextField {...params} label="Also email to" placeholder="Pick portal users" />}
          />
        )}
        {post.isError && <Alert severity="error">{errorMessage(post.error)}</Alert>}
        {remove.isError && <Alert severity="error">{errorMessage(remove.error)}</Alert>}
        <Stack direction="row" spacing={1} alignItems="center">
          {!notifyOpen && (
            <Button size="small" onClick={() => setNotifyOpen(true)}>Also email it…</Button>
          )}
          <Box sx={{ flex: 1 }} />
          <Button
            size="small"
            variant="contained"
            disabled={!body.trim() || post.isPending}
            onClick={() => post.mutate()}
          >
            {notify.length > 0 ? `Comment & email ${notify.length}` : "Comment"}
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
}
